import { useQuery } from '@tanstack/react-query';

import { Card } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatNumber } from '../../utils/formatters.js';

export function SalesReportsPage() {
  const { t } = useTranslation();
  const byCustomer = useQuery({
    queryKey: ['sales', 'reports', 'by-customer'],
    queryFn: () => salesApi.salesByCustomer().then((r) => r.items || []),
  });
  const byProduct = useQuery({
    queryKey: ['sales', 'reports', 'by-product'],
    queryFn: () => salesApi.salesByProduct().then((r) => r.items || []),
  });
  const funnel = useQuery({
    queryKey: ['sales', 'reports', 'funnel'],
    queryFn: () => salesApi.funnel().then((r) => r.items || []),
  });

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('sales.reports.title')}</h1>
          <p className="muted">{t('sales.reports.subtitle')}</p>
        </div>
      </header>

      <Card title={t('sales.reports.funnel')}>
        <Table
          isLoading={funnel.isLoading}
          rows={funnel.data}
          getRowKey={(row) => row.stage}
          columns={[
            { key: 'stage',           label: t('sales.reports.col.stage'),    render: (r) => t(`sales.pipeline.stage.${r.stage}`) },
            { key: 'count',           label: t('sales.reports.col.count'),    align: 'right', render: (r) => formatNumber(r.count, 0) },
            { key: 'amount',          label: t('sales.reports.col.pipeline'), align: 'right', render: (r) => formatCurrency(r.amount) },
            { key: 'weightedAmount',  label: t('sales.reports.col.weighted'), align: 'right', render: (r) => formatCurrency(r.weightedAmount) },
          ]}
        />
      </Card>

      <Card title={t('sales.reports.byCustomer')}>
        <Table
          isLoading={byCustomer.isLoading}
          rows={byCustomer.data}
          getRowKey={(row) => row.customerId}
          columns={[
            { key: 'code',       label: t('col.code') },
            { key: 'name',       label: t('col.name') },
            { key: 'orderCount', label: t('sales.reports.col.orders'),  align: 'right' },
            { key: 'revenue',    label: t('sales.reports.col.revenue'), align: 'right', render: (r) => formatCurrency(r.revenue) },
          ]}
        />
      </Card>

      <Card title={t('sales.reports.byProduct')}>
        <Table
          isLoading={byProduct.isLoading}
          rows={byProduct.data}
          getRowKey={(row) => row.productId}
          columns={[
            { key: 'productId',  label: t('col.product') },
            { key: 'quantity',   label: t('sales.reports.col.units'),   align: 'right', render: (r) => formatNumber(r.quantity) },
            { key: 'orderCount', label: t('sales.reports.col.orders'),  align: 'right' },
            { key: 'revenue',    label: t('sales.reports.col.revenue'), align: 'right', render: (r) => formatCurrency(r.revenue) },
          ]}
        />
      </Card>
    </section>
  );
}
